import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Box, Heading } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import cx from "classnames";
import styles from "./dropdownBox.module.scss";

interface DropdownBoxProps {
  className?: string;
  externalOpen?: boolean;
  title: string;
  /** Informational header content; must not contain interactive or focusable elements. */
  headerExtra?: ReactNode;
  children?: ReactNode;
  hasAction?: boolean;
}

const DropdownBox = ({
  className = "",
  externalOpen,
  title,
  headerExtra,
  children,
  hasAction = true,
}: DropdownBoxProps) => {
  const [isOpen, setIsOpen] = useState(externalOpen);

  const toggleDropdown = () => setIsOpen(!isOpen);

  useEffect(() => {
    setIsOpen(externalOpen);
  }, [externalOpen]);

  return (
    <Box className={cx(styles.dropdownBox, isOpen && styles.dropdownBox__isOpen, className)}>
      {!hasAction ? (
        <div className={styles.dropdownBox_head}>
          <Heading type="h3" className={styles.headTitle}>
            {title}
          </Heading>
          {headerExtra != null && <span className={styles.headExtra}>{headerExtra}</span>}
        </div>
      ) : (
        <button
          type="button"
          className={cx(
            styles.dropdownBox_head,
            headerExtra != null && styles.dropdownBox_head__withExtra,
          )}
          onClick={toggleDropdown}
          aria-expanded={!!isOpen}
        >
          <Heading type="h3" className={styles.headTitle}>
            {title}
          </Heading>

          {headerExtra != null && <span className={styles.headExtra}>{headerExtra}</span>}

          <Icon className={styles.headIcon} name="chevron-down" size="md" color="neutral-70" />
        </button>
      )}
      {hasAction && (
        <div
          className={styles.dropdownBox_content}
          aria-hidden={!isOpen}
          ref={element => {
            element?.toggleAttribute("inert", !isOpen);
          }}
        >
          {children}
        </div>
      )}
    </Box>
  );
};

export default DropdownBox;
